import moment from 'moment-timezone';

// SS-4222: the time-of-day windows of the api's /times `timepref` (controllers/bookings.js):
// a slot is offered when it starts and ends inside the window, on its own local day.
export const TIME_WINDOWS = {
  '': { starttime: 7, endtime: 20 },
  morning: { starttime: 7, endtime: 12 },
  afternoon: { starttime: 12, endtime: 20 },
};

// The api pairs a new-patient exam (service '0') with the cleaning that follows
// it, and a time-of-day preference must fit both - which the exam slot alone
// cannot tell. Only its generic availability path does that; these PMSs answer
// availability themselves and never pair, so their preference is applied here.
const NEVER_PAIRED_PMS = ['carestack', 'dentrixascend', 'denticon', 'cloud9'];

/**
 * Whether the preference can be applied to the Earliest list in the browser: always,
 * once the api has said which windows each slot fits (/times?timepref_fit=1, which
 * covers the cleaning of a paired visit). An api that does not say yet leaves a
 * paired new-patient visit to be filtered by the api, one fetch per preference.
 * `earliestSlots` is the unfiltered (Earliest) answer; only it can tell - an empty
 * Morning answer says nothing about the api.
 */
export const filtersLocally = (serviceId, pmsType, earliestSlots) => (
  String(serviceId).split(':')[0] !== '0' || NEVER_PAIRED_PMS.includes(pmsType)
  || (Array.isArray(earliestSlots) && earliestSlots.every(slot => slot.timepref_fit))
);

// the office's clock time on that day (not minutes since midnight, which a DST
// change would shift by an hour)
const atLocalHour = (dayStart, hour) => dayStart.clone()
  .hour(Math.floor(hour)).minute(Math.round((hour % 1) * 60)).second(0).millisecond(0).valueOf();

export const fitsTimeWindow = (slot, window, zone) => {
  const start = new Date(slot.date).getTime();
  const end = start + (Number(slot.duration) || 0);
  const dayStart = moment.tz(start, zone).startOf('day');
  return start >= atLocalHour(dayStart, window.starttime) && end <= atLocalHour(dayStart, window.endtime);
};

/**
 * The day summaries the widget renders (the shape of the api's multi-day /times
 * answer: date, day, and provider<k>_name / _img / _desc per provider), built from
 * the full slot list the widget loaded once, for one time-of-day preference. The
 * preference is applied here, so switching between Earliest / Morning / Afternoon
 * never asks the PMS for the same 40 days of times again.
 */
export const buildDaySummaries = (slots, timepref, officeZone) => {
  const zone = officeZone || moment.tz.guess();
  const window = TIME_WINDOWS[timepref] || TIME_WINDOWS[''];
  const days = new Map(); // YYYY-MM-DD -> Map(pid -> {name, img, times, count})
  const sorted = [...(slots || [])]
    // the api's own answer when it gave one (it covers a paired cleaning too)
    .filter(slot => (typeof slot.timepref_fit?.[timepref] === 'boolean'
      ? slot.timepref_fit[timepref]
      : fitsTimeWindow(slot, window, zone)))
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  for (const slot of sorted) {
    const local = moment.tz(slot.date, zone);
    const dayKey = local.format('YYYY-MM-DD');
    if (!days.has(dayKey)) {
      days.set(dayKey, new Map());
    }
    const providers = days.get(dayKey);
    if (!providers.has(slot.pid)) {
      providers.set(slot.pid, {
        name: (slot.title ? `${slot.title} ` : '') + slot.name,
        img: slot.img,
        times: [],
        count: 0,
      });
    }
    const provider = providers.get(slot.pid);
    provider.count += 1;
    if (provider.times.length < 3) {
      provider.times.push(local.format('h:mma'));
    }
  }
  return [...days.entries()].map(([dayKey, providers]) => {
    // local noon, so the date reads as the same calendar day in any US browser
    const noon = moment.tz(`${dayKey} 12:00`, 'YYYY-MM-DD HH:mm', zone);
    const summary = { date: noon.toDate(), day: noon.format('dddd') };
    [...providers.values()].forEach((provider, k) => {
      summary[`provider${k}_name`] = provider.name;
      summary[`provider${k}_img`] = provider.img;
      summary[`provider${k}_desc`] = provider.times.join(', ')
        + (provider.count > 3 ? ` and ${provider.count - 3} more.` : '');
    });
    return summary;
  });
};
